/**
 * Thin helper layer over ClarityService so call sites don't have to import
 * the singleton directly. All helpers are crash-safe: failures are logged and
 * swallowed so a bad analytics call never breaks a user flow.
 *
 * LGPD: callers MUST sanitise metadata. Pass IDs / enums / durations only —
 * never names, emails, phone numbers, or message contents. The sanitise step
 * below also drops any key matching FORBIDDEN_KEY_REGEX as a safety net.
 *
 * See: documentation/01. Platform/PRD_CLARITY_TAGGING.md
 */

import AsyncStorage from '@react-native-async-storage/async-storage';

// ─── Public types ────────────────────────────────────────────────────────────

/**
 * Macro events — the 14 conversion milestones. These are the only events
 * allowed to define cross-flow funnels in the Clarity dashboard.
 *
 * NOTE: `subscription_purchase_started` has no canonical fire site in Phase 1.
 * The native RC paywall handles `Purchases.purchasePackage()` internally with
 * no JS-observable "user committed" callback. Wiring requires the RC
 * `purchasesStartedListener` (Phase 2). The helper exists so call sites can
 * fire it once that listener is wired.
 */
export type ClarityMacroEventName =
  | 'app_opened'
  | 'auth_signup_completed'
  | 'auth_login_succeeded'
  | 'onboarding_completed'
  | 'consent_accepted'
  | 'bill_first_message_sent'
  | 'mood_first_logged'
  | 'meeting_joined'
  | 'community_first_post_published'
  | 'missoes_first_completed'
  | 'subscription_paywall_viewed'
  | 'subscription_purchase_started'
  | 'subscription_purchase_succeeded'
  | 'subscription_purchase_failed'
  | 'csat_submitted';

/**
 * Micro events — in-flow signals that answer "where in this flow did the user
 * drop off?". They live BENEATH macro events in the funnel hierarchy. New
 * micro event names MUST be added here AND to PRD_CLARITY_TAGGING.md §5 in
 * the same change.
 *
 * See SYS_CLARITY_TAGGING.md for the canonical-site rule, the dynamic-import
 * call pattern, and the pre-merge checklist that gates new wiring.
 */
export type ClarityMicroEventName =
  // Auth (PRD §5.1)
  | 'auth_first_access_started'
  | 'auth_first_access_email_submitted'
  | 'auth_verify_code_submitted'
  | 'auth_set_password_submitted'
  | 'auth_forgot_password_requested'
  | 'auth_magic_link_opened'
  | 'auth_login_failed'
  | 'auth_biometric_prompt_shown'
  | 'auth_biometric_enrolled'
  // Onboarding (PRD §5.2)
  | 'onboarding_step_started'
  | 'onboarding_step_completed'
  | 'onboarding_step_skipped'
  | 'onboarding_back_pressed'
  // Bill (PRD §5.3)
  | 'bill_message_sent'
  | 'bill_response_received'
  | 'bill_response_failed'
  | 'bill_voice_record_started'
  | 'bill_voice_record_completed'
  | 'bill_quick_action_tapped'
  // Diary / mood (PRD §5.4)
  | 'mood_entry_started'
  | 'mood_entry_submitted'
  | 'mood_entry_abandoned'
  | 'diary_note_submitted'
  | 'diary_tab_viewed'
  | 'missoes_viewed'
  | 'missoes_started'
  | 'missoes_completed'
  // Sobriety (PRD §5.5)
  | 'sobriety_clock_viewed'
  | 'sobriety_input_submitted'
  | 'sobriety_relapse_logged'
  | 'sobriety_share_tapped'
  // Meetings (PRD §5.6)
  | 'meeting_list_viewed'
  | 'meeting_detail_viewed'
  | 'meeting_favorite_toggled'
  | 'meeting_join_tapped'
  | 'meeting_checkin_submitted'
  | 'suggestion_card_tapped'
  // Community (PRD §5.7)
  | 'community_feed_viewed'
  | 'community_post_viewed'
  | 'community_post_liked'
  | 'community_comment_submitted'
  | 'community_post_published'
  // Trail (PRD §5.8)
  | 'trail_section_opened'
  | 'trail_step_started'
  | 'trail_step_completed'
  // Sponsor (PRD §5.9)
  | 'sponsor_list_viewed'
  | 'sponsor_detail_viewed'
  | 'sponsor_call_requested'
  | 'appointment_checkout_started'
  | 'appointment_checkout_succeeded'
  | 'appointment_checkout_cancelled'
  | 'appointment_checkout_failed'
  | 'appointment_checkout_resume_tapped'
  | 'appointment_checkout_resumed'
  | 'appointment_checkout_expired'
  | 'appointment_checkout_refund_started'
  | 'appointment_checkout_refund_completed'
  // Notifications (PRD §5.10)
  | 'notification_opened'
  | 'notification_settings_changed'
  // Settings / account (PRD §5.11)
  | 'settings_opened'
  | 'settings_changed'
  | 'account_delete_requested'
  | 'account_deleted'
  // Subscription (PRD §5.12)
  | 'subscription_paywall_dismissed'
  | 'subscription_package_selected'
  | 'subscription_restore_tapped'
  // CSAT (PRD §5.13)
  | 'csat_displayed'
  | 'csat_dismissed';

/** Union of every typed event name. */
export type ClarityEventName = ClarityMacroEventName | ClarityMicroEventName;

/**
 * Allowed metadata keys. New keys MUST be added here AND to the dictionary
 * in PRD_CLARITY_TAGGING.md §7 in the same change.
 */
export type ClarityMetadataKey =
  | 'result'
  | 'step'
  | 'from_surface'
  | 'surface'
  | 'element'
  | 'action'
  | 'variant'
  | 'error_code'
  | 'duration_ms'
  | 'latency_ms'
  | 'is_plus'
  | 'is_returning_user'
  | 'clinic_id'
  | 'locale'
  | 'method'
  | 'meeting_source'
  | 'meeting_type'
  | 'tab'
  | 'package_id'
  | 'score'
  | 'mood_value'
  | 'feeling_count'
  | 'has_note'
  | 'length_bucket'
  | 'topic_id'
  | 'post_id'
  | 'meeting_id'
  | 'sponsor_id'
  | 'step_id'
  | 'section_id'
  | 'category'
  | 'field'
  | 'enabled'
  | 'provider'
  | 'tokens_estimated'
  | 'terms_version'
  | 'privacy_version'
  | 'channel'
  | 'addiction_type'
  | 'frequency'
  | 'goal_type'
  | 'message_ix_in_session'
  | 'uploaded_photo'
  | 'start_type'
  | 'action_id'
  | 'theme_count'
  | 'days_set'
  | 'previous_streak_days'
  | 'last_step'
  | 'mission_id';

/** Looser type for the public escape-hatch helper. */
type ClarityRawMetadata = Record<string, string | number | boolean | undefined | null>;

type ClarityServiceModule = typeof import('@/services/clarityService');
type ClarityInstance = ReturnType<ClarityServiceModule['default']['getInstance']>;

type GoogleAnalyticsServiceModule = typeof import('@/services/googleAnalyticsService');
type GoogleAnalyticsInstance = GoogleAnalyticsServiceModule['GoogleAnalyticsService'];

// ─── Internal: forbidden keys ────────────────────────────────────────────────

/**
 * Hard block any key that looks like PII or free-text content. Matched
 * case-insensitively against the raw key name. This is a safety net — call
 * sites should never pass these in the first place.
 */
const FORBIDDEN_KEY_REGEX =
  /(^|_)(name|email|phone|cpf|rg|address|cep|message|note|post|body|content|text|password|token|secret|fullname|firstname|lastname|displayname|username)($|_)/i;

const FORBIDDEN_VALUE_AS_KEY = new Set([
  'fullName',
  'firstName',
  'lastName',
  'displayName',
  'userName',
]);

function isForbiddenKey(key: string): boolean {
  if (FORBIDDEN_VALUE_AS_KEY.has(key)) return true;
  return FORBIDDEN_KEY_REGEX.test(key);
}

// ─── Internal: crash-safe runner ─────────────────────────────────────────────

async function withClarity<T>(fn: (svc: ClarityInstance) => Promise<T> | T): Promise<void> {
  try {
    const ClarityService = await import('@/services/clarityService');
    await Promise.resolve(fn(ClarityService.default.getInstance()));
  } catch {
    // Non-fatal; service-level guards already log warnings on real failures.
  }
}

/**
 * Tier 1 fan-out runner for Firebase Analytics (GA4). Used ONLY by the
 * conversion-event helpers below (`logAuthSignupCompleted`,
 * `logAuthLoginSucceeded`, `logSubscriptionPaywallViewed`). Crash-safe and
 * lazy — same pattern as `withClarity`.
 *
 * Why not fan out every event? Per `documentation/01. Platform/SYS_GOOGLE_ADS_SDK.md`,
 * Firebase data feeds Google's ad-targeting graph. Recovery / clinical
 * events (sobriety, mood, Bill, community, meetings) MUST stay
 * Clarity-only to avoid leaking health context into ad audiences.
 */
async function withFirebase(
  fn: (svc: GoogleAnalyticsInstance) => Promise<void> | void
): Promise<void> {
  try {
    const mod = await import('@/services/googleAnalyticsService');
    await Promise.resolve(fn(mod.GoogleAnalyticsService));
  } catch {
    // Non-fatal; service-level guards already log warnings on real failures.
  }
}

// ─── Internal: metadata sanitiser ────────────────────────────────────────────

/**
 * Sanitises a metadata bag down to primitive scalars. Drops:
 *  - undefined / null values (so encoded event names stay short)
 *  - non-primitive values (objects, arrays, functions)
 *  - keys matching FORBIDDEN_KEY_REGEX (LGPD safety net) — emits console.warn
 */
function sanitize(
  eventName: string,
  metadata?: ClarityRawMetadata
): Record<string, string | number | boolean> | undefined {
  if (!metadata) return undefined;
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(metadata)) {
    if (v === undefined || v === null) continue;
    if (isForbiddenKey(k)) {
      console.warn(
        `[clarityEvents] Dropped forbidden metadata key "${k}" on event "${eventName}" (LGPD)`
      );
      continue;
    }
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
      out[k] = v;
    }
  }
  return Object.keys(out).length ? out : undefined;
}

// ─── Internal: first-ever flag helper ────────────────────────────────────────

const FIRST_EVER_KEY_PREFIX = '@padrinho/clarity/first-ever:';

/**
 * Idempotent "fire only on first ever occurrence" helper. Persists a flag in
 * AsyncStorage so subsequent calls become no-ops. Used by the *_first_*
 * macro events. Cheap, sufficient for analytics — not a strict source of
 * truth for "first" semantics.
 */
async function fireFirstEverOnly(
  eventName: ClarityMacroEventName,
  metadata: ClarityRawMetadata | undefined,
  flagKey: string
): Promise<void> {
  try {
    const storageKey = `${FIRST_EVER_KEY_PREFIX}${flagKey}`;
    const already = await AsyncStorage.getItem(storageKey);
    if (already) return;
    await AsyncStorage.setItem(storageKey, '1');
  } catch {
    // If AsyncStorage is unavailable, fall through and emit anyway. Analytics
    // dedup is best-effort, not critical.
  }
  await withClarity((svc) => svc.logEvent(eventName, sanitize(eventName, metadata)));
}

// ─── Public: page-name helpers (unchanged) ───────────────────────────────────

export async function setClarityPageName(name: string): Promise<void> {
  await withClarity((svc) => svc.setCustomPageName(name));
}

/**
 * Maps an expo-router pathname to a friendly Clarity page name. Falls back to
 * a humanised version of the raw path when no explicit mapping exists, so
 * unmapped routes still get something readable in the dashboard.
 */
export function pageNameForRoute(pathname: string): string {
  if (!pathname) return 'Unknown';

  // Exact matches first
  const exact: Record<string, string> = {
    '/': 'Home · Bill Chat',
    '/welcome': 'Welcome',
    '/login': 'Auth · Login',
    '/first-access': 'Auth · First access',
    '/verify-code': 'Auth · Verify code',
    '/set-password': 'Auth · Set password',
    '/forgot-password': 'Auth · Forgot password',
    '/reset-password': 'Auth · Reset password',
    '/change-password': 'Settings · Change password',
    '/change-email': 'Settings · Change email',
    '/change-name': 'Settings · Change name',
    '/change-phone': 'Settings · Change phone',
    '/change-username': 'Settings · Change username',
    '/change-goal': 'Settings · Change goal',
    '/email-success': 'Settings · Email updated',
    '/magic-link-login': 'Auth · Magic link',
    '/delete-account': 'Settings · Delete account',
    '/delete-account-confirmation': 'Settings · Delete account confirmation',
    '/notifications': 'Notifications',
    '/notification-settings': 'Settings · Notifications',
    '/language-settings': 'Settings · Language',
    '/interests-settings': 'Settings · Interests',
    '/perfil': 'Profile',
    '/subscription': 'Subscription',
    '/onboarding-welcome': 'Onboarding · Welcome',
    '/onboarding-name': 'Onboarding · Name',
    '/onboarding-gender': 'Onboarding · Gender',
    '/onboarding-goal': 'Onboarding · Goal',
    '/onboarding-addiction-type': 'Onboarding · Addiction type',
    '/onboarding-frequency': 'Onboarding · Frequency',
    '/onboarding-interests': 'Onboarding · Interests',
    '/onboarding-sponsorship': 'Onboarding · Sponsorship',
    '/onboarding-notifications': 'Onboarding · Notifications',
    '/onboarding-success': 'Onboarding · Success',
    '/diary': 'Diary',
    '/add-note': 'Diary · Add note',
    '/diario-emocional': 'Mood diary',
    '/diario-humor': 'Mood diary · Mood',
    '/diario-nota': 'Mood diary · Note',
    '/diario-sentimento': 'Mood diary · Feeling',
    '/diario-sentimento-categoria': 'Mood diary · Feeling category',
    '/diario-sentimento-nota': 'Mood diary · Feeling note',
    '/diario-temas': 'Mood diary · Topics',
    '/community': 'Community',
    '/community-chat': 'Community · Chat',
    '/community-new-post': 'Community · New post',
    '/community-post': 'Community · Post',
    '/community-profile': 'Community · Profile',
    '/community-topics': 'Community · Topics',
    '/encontros-online': 'Meetings · Online',
    '/encontros-onboarding': 'Meetings · Onboarding',
    '/add-meeting': 'Meetings · Add',
    '/missoes-history': 'Missions · History',
    '/sobriety-clock': 'Sobriety · Clock',
    '/sobriety-input': 'Sobriety · Input',
    '/sobriety-relapse': 'Sobriety · Relapse',
    '/sobriety-congratulations': 'Sobriety · Congratulations',
    '/share-sobriety': 'Sobriety · Share',
    '/widget-guide': 'Widget · Guide',
    '/widget-preview': 'Widget · Preview',
    '/audio-player': 'Audio player',
    '/csat': 'CSAT',
    '/patient-welcome': 'Patient · Welcome',
    '/provider': 'Provider',
    '/webview': 'Webview',
    '/admin-prompt-config': 'Admin · Prompt config',
    '/video-call/lobby': 'Meeting · Lobby',
    '/video-call/permissions': 'Meeting · Permissions',
    '/video-call/postcall': 'Meeting · Post-call',
    '/sponsors/payment': 'Sponsors · Payment review',
  };

  if (exact[pathname]) return exact[pathname];

  // Prefix-based fallbacks
  if (pathname.startsWith('/video-call/')) return 'Meeting · Room';
  if (pathname.startsWith('/meeting/')) return 'Meeting · Details';
  if (pathname.startsWith('/sponsors/')) return 'Sponsors · Detail';
  if (pathname === '/sponsors' || pathname.startsWith('/sponsors')) return 'Sponsors';
  if (pathname.startsWith('/group/')) return 'Group · Detail';
  if (pathname.startsWith('/trail/category/')) return 'Trail · Category';
  if (pathname.startsWith('/trail/') && pathname.split('/').length > 3) return 'Trail · Step';
  if (pathname.startsWith('/trail/')) return 'Trail · Section';
  if (pathname === '/trail') return 'Trail';

  // Generic fallback: "/some-thing/here" → "Some Thing · Here"
  const cleaned = pathname.replace(/^\//, '').replace(/\/+$/g, '');
  if (!cleaned) return 'Home · Bill Chat';
  return cleaned
    .split('/')
    .map((seg) => seg.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()))
    .join(' · ');
}

// ─── Public: raw escape hatches (Phase 2 micro events use these) ─────────────

/**
 * Raw event emitter. Prefer the typed macro helpers below for funnel events.
 * Use this for micro events (PRD §5) and nano events (PRD §6) until typed
 * helpers exist for them.
 */
export async function logClarityEvent(name: string, metadata?: ClarityRawMetadata): Promise<void> {
  await withClarity((svc) => svc.logEvent(name, sanitize(name, metadata)));
}

// ─── Public: typed length-bucket helper ──────────────────────────────────────

/**
 * Bucket a free-text length into a coarse enum so we can analyse usage
 * patterns without exposing the content itself.
 */
export function lengthBucket(charCount: number): 'short' | 'medium' | 'long' {
  if (charCount <= 80) return 'short';
  if (charCount <= 280) return 'medium';
  return 'long';
}

// ─── Public: 14 typed macro helpers (PRD §4) ─────────────────────────────────

/** App cold start, after fonts load. */
export async function logAppOpened(metadata: {
  is_returning_user: boolean;
  locale?: 'pt' | 'en' | 'es';
  start_type?: 'cold' | 'warm';
}): Promise<void> {
  await withClarity((svc) => svc.logEvent('app_opened', sanitize('app_opened', metadata)));
}

/**
 * Brand-new user finishes set-password (sign-in + consent both ok).
 *
 * Tier 1 fan-out: also fires GA4 standard `sign_up` event for Google Ads
 * conversion attribution. See SYS_GOOGLE_ADS_SDK.md.
 */
export async function logAuthSignupCompleted(metadata: {
  method: 'email' | 'magic_link';
  clinic_id?: number;
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('auth_signup_completed', sanitize('auth_signup_completed', metadata))
  );
  await withFirebase((ga) => ga.logSignUp(metadata.method));
}

/**
 * Returning user authenticates successfully.
 *
 * Tier 1 fan-out: also fires GA4 standard `login` event so GA4 retention
 * cohorts (Day 1 / 7 / 30) work out of the box. Not a conversion goal.
 */
export async function logAuthLoginSucceeded(metadata: {
  method: 'email' | 'magic_link' | 'biometric';
  clinic_id?: number;
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('auth_login_succeeded', sanitize('auth_login_succeeded', metadata))
  );
  await withFirebase((ga) => ga.logLogin(metadata.method));
}

/** User reaches /onboarding-success and taps "go to home". */
export async function logOnboardingCompleted(metadata: {
  addiction_type?: string;
  frequency?: string;
  goal_type?: string;
  duration_ms?: number;
  uploaded_photo?: boolean;
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('onboarding_completed', sanitize('onboarding_completed', metadata))
  );
}

/** User accepts terms + privacy in the post-login modal queue or signup flow. */
export async function logConsentAccepted(metadata: {
  terms_version: string;
  privacy_version: string;
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('consent_accepted', sanitize('consent_accepted', metadata))
  );
}

/** First user-authored message ever written to Bill (idempotent across sessions). */
export async function logBillFirstMessageSent(metadata: { from_surface: string }): Promise<void> {
  await fireFirstEverOnly('bill_first_message_sent', metadata, 'bill_message');
}

/** First mood diary entry written by the user (idempotent across sessions). */
export async function logMoodFirstLogged(metadata: { mood_value?: number }): Promise<void> {
  await fireFirstEverOnly('mood_first_logged', metadata, 'mood_entry');
}

/** User enters a meeting room (native or external link). */
export async function logMeetingJoined(metadata: {
  meeting_source: 'elo' | 'supabase' | 'external';
  meeting_type: 'video' | 'external' | 'appointment';
  meeting_id?: string;
}): Promise<void> {
  await withClarity((svc) => svc.logEvent('meeting_joined', sanitize('meeting_joined', metadata)));
}

/** User's first ever published community post (idempotent across sessions). */
export async function logCommunityFirstPostPublished(metadata: {
  topic_id?: string;
}): Promise<void> {
  await fireFirstEverOnly('community_first_post_published', metadata, 'community_post');
}

/**
 * Maps the raw `from_surface` value Clarity receives down to one of four
 * recovery-context-neutral buckets that are safe to send to Google's ad
 * graph. Per `documentation/01. Platform/SYS_GOOGLE_ADS_SDK.md`, GA4
 * payloads must NEVER carry strings that reveal addiction / sobriety /
 * meeting / community context — Google uses event params as audience
 * signals for ad targeting.
 *
 * Buckets:
 *  - `settings`     — paywall opened from the user's own profile / settings
 *  - `content_gate` — paywall blocking access to a Plus-gated feature
 *  - `onboarding`   — paywall in the new-user flow
 *  - `other`        — fallback (also covers `unknown`)
 *
 * Clarity still receives the raw, specific surface name (clinical analysis
 * is allowed there). Only the GA fan-out is neutralised.
 */
function neutralizePaywallSurface(
  raw: string
): 'settings' | 'content_gate' | 'onboarding' | 'other' {
  switch (raw) {
    case 'subscription_screen':
    case 'profile':
      return 'settings';
    case 'trail_start_sheet':
    case 'mission_history':
    case 'meeting_detail':
      return 'content_gate';
    case 'onboarding_end':
    case 'onboarding_success':
      return 'onboarding';
    default:
      return 'other';
  }
}

/**
 * Paywall screen renders.
 *
 * Tier 1 fan-out: also fires GA4 standard `view_promotion` event so we can
 * build remarketing audiences of users who reached the paywall but didn't
 * buy. Skipped when the user is already Plus (would skew funnel + audience).
 *
 * The raw `from_surface` (which can encode recovery context like
 * `trail_start_sheet` or `meeting_detail`) is mapped to a neutral bucket
 * before reaching GA — see `neutralizePaywallSurface()`.
 */
export async function logSubscriptionPaywallViewed(metadata: {
  from_surface: string;
  is_plus: boolean;
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('subscription_paywall_viewed', sanitize('subscription_paywall_viewed', metadata))
  );
  if (!metadata.is_plus) {
    const safeSurface = neutralizePaywallSurface(metadata.from_surface);
    await withFirebase((ga) => ga.logViewPromotion(safeSurface));
  }
}

/**
 * User taps "Continue" on a package inside the paywall.
 * NOT WIRED in Phase 1 — the native RC paywall has no JS-observable callback
 * for package commit. Wiring requires `Purchases.addPurchasesStartedListener`
 * (Phase 2 of PRD §9). This helper exists so a future call site can fire it
 * without re-touching the helpers file.
 */
export async function logSubscriptionPurchaseStarted(metadata: {
  package_id: string;
  from_surface: string;
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent(
      'subscription_purchase_started',
      sanitize('subscription_purchase_started', metadata)
    )
  );
}

/** RevenueCat returns Plus entitlement (PURCHASED or RESTORED). */
export async function logSubscriptionPurchaseSucceeded(metadata: {
  package_id?: string;
  from_surface: string;
  duration_ms?: number;
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent(
      'subscription_purchase_succeeded',
      sanitize('subscription_purchase_succeeded', metadata)
    )
  );
}

/** RC error or user cancellation inside the paywall. */
export async function logSubscriptionPurchaseFailed(metadata: {
  error_code: string;
  package_id?: string;
  from_surface?: string;
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('subscription_purchase_failed', sanitize('subscription_purchase_failed', metadata))
  );
}

/** User submits a CSAT (post-call or general). */
export async function logCsatSubmitted(metadata: {
  score: number;
  meeting_type?: 'video' | 'external' | 'appointment';
}): Promise<void> {
  await withClarity((svc) => svc.logEvent('csat_submitted', sanitize('csat_submitted', metadata)));
}

// ─── Public: typed micro helpers (PRD §5) ────────────────────────────────────

/**
 * User reaches an onboarding step (mount of one of `app/onboarding-*.tsx`).
 * Fired by `hooks/useOnboardingStepTracking.ts` — do not call directly from
 * a screen.
 *
 * `step` enum (PRD §5.2): name | gender | goal | addiction_type | frequency
 *                         | interests | sponsorship | notifications.
 */
export async function logOnboardingStepStarted(metadata: {
  step:
    | 'name'
    | 'gender'
    | 'support_recipient'
    | 'goal'
    | 'addiction_type'
    | 'frequency'
    | 'interests'
    | 'sponsorship'
    | 'notifications';
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('onboarding_step_started', sanitize('onboarding_step_started', metadata))
  );
}

/**
 * Bill AI returns a successful response from the server.
 * Canonical site: `contexts/BillContext.tsx` — fired AFTER the empty-response
 * guard so success-rate analytics aren't polluted by empty responses (those
 * fall through to `bill_response_failed`).
 *
 * `provider` is the LLM that served the response (server-set enum).
 */
export async function logBillResponseReceived(metadata: {
  latency_ms: number;
  provider: 'anthropic' | 'openai' | 'gemini' | 'unknown';
  tokens_estimated?: number;
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('bill_response_received', sanitize('bill_response_received', metadata))
  );
}

/**
 * Bill AI fails to return a response (network error, server error, empty
 * response). Canonical site: `contexts/BillContext.tsx` catch-block.
 *
 * `error_code` should be a stable identifier (HTTP status, error class name,
 * or sentinel like `empty_response` / `network` / `timeout`). NEVER pass the
 * raw error message — it can leak server-side detail.
 */
export async function logBillResponseFailed(metadata: {
  error_code: string;
  latency_ms?: number;
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('bill_response_failed', sanitize('bill_response_failed', metadata))
  );
}

/**
 * Bill proactive welcome sequence shown to user on first open or returning session.
 * `variant` distinguishes the 3-bubble new-user sequence from the single returning-user bubble.
 */
export async function logBillWelcomeShown(metadata: {
  variant: 'new' | 'returning';
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('bill_welcome_shown', sanitize('bill_welcome_shown', metadata))
  );
}

/**
 * User dismisses the RC paywall without buying. Fires alongside the macro
 * `subscription_purchase_failed` (with `error_code='user_cancelled'`) on the
 * cancellation branch — they're complementary signals: the macro is the
 * funnel step, the micro is the dismissal volume across all surfaces.
 *
 * Canonical site: `services/revenueCatService.ts` `presentPaywall` →
 * `PAYWALL_RESULT.CANCELLED` branch.
 */
export async function logSubscriptionPaywallDismissed(metadata: {
  from_surface: string;
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent(
      'subscription_paywall_dismissed',
      sanitize('subscription_paywall_dismissed', metadata)
    )
  );
}

// ─── Public: Phase 2 micro helpers — Auth (PRD §5.1) ────────────────────────

export async function logAuthFirstAccessStarted(): Promise<void> {
  await withClarity((svc) => svc.logEvent('auth_first_access_started'));
}

export async function logAuthFirstAccessEmailSubmitted(metadata: {
  result: 'new_user' | 'existing_user' | 'error';
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent(
      'auth_first_access_email_submitted',
      sanitize('auth_first_access_email_submitted', metadata)
    )
  );
}

export async function logAuthVerifyCodeSubmitted(metadata: {
  result: 'success' | 'invalid' | 'expired';
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('auth_verify_code_submitted', sanitize('auth_verify_code_submitted', metadata))
  );
}

export async function logAuthSetPasswordSubmitted(metadata: {
  result: 'success' | 'failed';
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('auth_set_password_submitted', sanitize('auth_set_password_submitted', metadata))
  );
}

export async function logAuthForgotPasswordRequested(): Promise<void> {
  await withClarity((svc) => svc.logEvent('auth_forgot_password_requested'));
}

export async function logAuthMagicLinkOpened(): Promise<void> {
  await withClarity((svc) => svc.logEvent('auth_magic_link_opened'));
}

export async function logAuthLoginFailed(metadata: {
  error_code: 'invalid_credentials' | 'network' | 'other';
  method?: 'email' | 'magic_link' | 'biometric';
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('auth_login_failed', sanitize('auth_login_failed', metadata))
  );
}

export async function logAuthBiometricPromptShown(): Promise<void> {
  await withClarity((svc) => svc.logEvent('auth_biometric_prompt_shown'));
}

export async function logAuthBiometricEnrolled(metadata: {
  result: 'enrolled' | 'declined';
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('auth_biometric_enrolled', sanitize('auth_biometric_enrolled', metadata))
  );
}

// ─── Public: Phase 2 micro helpers — Onboarding (PRD §5.2) ───────────────────

/** Onboarding step name enum, shared by step_started/completed/skipped/back_pressed. */
export type OnboardingStepName =
  | 'name'
  | 'gender'
  | 'support_recipient'
  | 'goal'
  | 'addiction_type'
  | 'frequency'
  | 'interests'
  | 'sponsorship'
  | 'notifications';

export async function logOnboardingStepCompleted(metadata: {
  step: OnboardingStepName;
  duration_ms?: number;
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('onboarding_step_completed', sanitize('onboarding_step_completed', metadata))
  );
}

export async function logOnboardingStepSkipped(metadata: {
  step: OnboardingStepName;
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('onboarding_step_skipped', sanitize('onboarding_step_skipped', metadata))
  );
}

export async function logOnboardingBackPressed(metadata: {
  step: OnboardingStepName;
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('onboarding_back_pressed', sanitize('onboarding_back_pressed', metadata))
  );
}

// ─── Public: Phase 2 micro helpers — Bill (PRD §5.3) ─────────────────────────

export async function logBillMessageSent(metadata: {
  message_ix_in_session: number;
  is_plus: boolean;
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('bill_message_sent', sanitize('bill_message_sent', metadata))
  );
}

export async function logBillVoiceRecordStarted(): Promise<void> {
  await withClarity((svc) => svc.logEvent('bill_voice_record_started'));
}

export async function logBillVoiceRecordCompleted(metadata: {
  duration_ms: number;
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('bill_voice_record_completed', sanitize('bill_voice_record_completed', metadata))
  );
}

export async function logBillQuickActionTapped(metadata: { action_id: string }): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('bill_quick_action_tapped', sanitize('bill_quick_action_tapped', metadata))
  );
}

// ─── Public: Phase 2 micro helpers — Diary / mood (PRD §5.4) ─────────────────

export async function logMoodEntryStarted(): Promise<void> {
  await withClarity((svc) => svc.logEvent('mood_entry_started'));
}

export async function logMoodEntrySubmitted(metadata: {
  mood_value?: number;
  feeling_count?: number;
  has_note?: boolean;
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('mood_entry_submitted', sanitize('mood_entry_submitted', metadata))
  );
}

export async function logMoodEntryAbandoned(metadata: { last_step: string }): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('mood_entry_abandoned', sanitize('mood_entry_abandoned', metadata))
  );
}

export async function logDiaryNoteSubmitted(metadata: {
  length_bucket: 'short' | 'medium' | 'long';
  theme_count?: number;
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('diary_note_submitted', sanitize('diary_note_submitted', metadata))
  );
}

/**
 * Canonical mission keys — mirrors `MissionKey` in
 * `services/recoveryPlanService.ts`. Kept as a local literal union so this
 * helpers file stays free of a runtime dependency on the service module.
 * If a key is added or reordered there, mirror the change here AND in
 * PRD §7 (`mission_id` row).
 */
export type MissionId =
  | 'participar_encontro'
  | 'registrar_diario_sentimentos'
  | 'progredir_trilha'
  | 'apoiar_membro_comunidade'
  | 'publicar_comunidade';

/**
 * Active diary sub-tab. Fires on every tab change in `app/diary.tsx`,
 * including the first-mount default (`'missoes'`). Gives the dashboard
 * a real per-tab entry-step instead of leaning on the global `Diary`
 * page-view count.
 */
export async function logDiaryTabViewed(metadata: {
  tab: 'missoes' | 'relogio' | 'notas';
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('diary_tab_viewed', sanitize('diary_tab_viewed', metadata))
  );
}

/**
 * Missões tab specifically becomes active. Pairs with `diary_tab_viewed`
 * (which fires for all three tabs); this one is the area-specific filter
 * row REF dashboard §7.2 funnel-step 2 references.
 */
export async function logMissoesViewed(): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('missoes_viewed', sanitize('missoes_viewed', { tab: 'missoes' }))
  );
}

/**
 * User taps a pending mission row, intending to fulfil it. Canonical site:
 * `components/MissionRow.tsx` `handlePress` (intent point — fires BEFORE
 * the `router.push` to the underlying surface so intent volume is captured
 * even if navigation aborts).
 */
export async function logMissoesStarted(metadata: { mission_id: MissionId }): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('missoes_started', sanitize('missoes_started', metadata))
  );
}

/**
 * A mission flipped from pending → completed since the previous fetch.
 * Detection lives in `app/diary.tsx` `loadMissoes` success branch: the
 * server is the source of truth (missions are completed implicitly when
 * the user logs the underlying activity), so we diff the new vs previous
 * mission set and fire one event per `false → true` transition. The
 * baseline is local-date scoped, so day rollover resets it to avoid
 * cross-day misclassification.
 *
 * `duration_ms` is omitted in the current implementation — the screen does
 * not capture a per-mission start timestamp, and the underlying activity
 * (sentimento, trail step, community post) can complete in another tab.
 * Add when a real start-to-complete bracket exists.
 */
export async function logMissoesCompleted(metadata: {
  mission_id: MissionId;
  duration_ms?: number;
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('missoes_completed', sanitize('missoes_completed', metadata))
  );
}

/**
 * First mission EVER completed by this install (any `mission_id`). Macro
 * adoption signal for the Missões area. Idempotent across sessions via
 * `fireFirstEverOnly` (flag key `mission_completion`).
 *
 * The diff loop in `app/diary.tsx` `loadMissoes` only invokes this helper
 * for AT MOST ONE mission per load cycle — if multiple missions flip in
 * the same diff, the loop picks the first transitioning mission and
 * skips the rest. This avoids a `getItem`/`setItem` race on the
 * AsyncStorage flag inside `fireFirstEverOnly` that would otherwise emit
 * duplicate macro events. The "first mission ever" semantic is preserved
 * because there is, by definition, only one such event.
 */
export async function logMissoesFirstCompleted(metadata: { mission_id: MissionId }): Promise<void> {
  await fireFirstEverOnly('missoes_first_completed', metadata, 'mission_completion');
}

// ─── Public: Phase 2 micro helpers — Sobriety (PRD §5.5) ─────────────────────

export async function logSobrietyClockViewed(): Promise<void> {
  await withClarity((svc) => svc.logEvent('sobriety_clock_viewed'));
}

export async function logSobrietyInputSubmitted(metadata: { days_set: number }): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('sobriety_input_submitted', sanitize('sobriety_input_submitted', metadata))
  );
}

export async function logSobrietyRelapseLogged(metadata: {
  previous_streak_days: number;
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('sobriety_relapse_logged', sanitize('sobriety_relapse_logged', metadata))
  );
}

export async function logSobrietyShareTapped(): Promise<void> {
  await withClarity((svc) => svc.logEvent('sobriety_share_tapped'));
}

// ─── Public: Phase 2 micro helpers — Meetings (PRD §5.6) ─────────────────────

export async function logMeetingListViewed(metadata: { tab: string }): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('meeting_list_viewed', sanitize('meeting_list_viewed', metadata))
  );
}

export async function logMeetingDetailViewed(metadata: {
  meeting_source: 'elo' | 'supabase' | 'external';
  meeting_type: 'video' | 'external' | 'appointment';
  meeting_id?: string;
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('meeting_detail_viewed', sanitize('meeting_detail_viewed', metadata))
  );
}

export async function logMeetingFavoriteToggled(metadata: {
  meeting_source: 'elo' | 'supabase' | 'external';
  result: 'saved' | 'unsaved';
  meeting_id?: string;
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('meeting_favorite_toggled', sanitize('meeting_favorite_toggled', metadata))
  );
}

export async function logMeetingJoinTapped(metadata: {
  meeting_source: 'elo' | 'supabase' | 'external';
  meeting_type: 'video' | 'external' | 'appointment';
  meeting_id?: string;
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('meeting_join_tapped', sanitize('meeting_join_tapped', metadata))
  );
}

export async function logMeetingCheckinSubmitted(metadata: {
  meeting_source: 'elo' | 'supabase' | 'external';
  result: 'success' | 'failed';
  meeting_id?: string;
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('meeting_checkin_submitted', sanitize('meeting_checkin_submitted', metadata))
  );
}

// ─── Public: Phase 2 micro helpers — Community (PRD §5.7) ────────────────────

export async function logCommunityFeedViewed(metadata: { tab: string }): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('community_feed_viewed', sanitize('community_feed_viewed', metadata))
  );
}

export async function logCommunityPostViewed(metadata: { post_id: string }): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('community_post_viewed', sanitize('community_post_viewed', metadata))
  );
}

export async function logCommunityPostLiked(metadata: { post_id: string }): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('community_post_liked', sanitize('community_post_liked', metadata))
  );
}

export async function logCommunityCommentSubmitted(metadata: { post_id: string }): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('community_comment_submitted', sanitize('community_comment_submitted', metadata))
  );
}

export async function logCommunityPostPublished(metadata: {
  topic_id?: string;
  length_bucket?: 'short' | 'medium' | 'long';
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('community_post_published', sanitize('community_post_published', metadata))
  );
}

// ─── Public: Phase 2 micro helpers — Trail (PRD §5.8) ────────────────────────

export async function logTrailSectionOpened(metadata: { section_id: string }): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('trail_section_opened', sanitize('trail_section_opened', metadata))
  );
}

export async function logTrailStepStarted(metadata: { step_id: string }): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('trail_step_started', sanitize('trail_step_started', metadata))
  );
}

export async function logTrailStepCompleted(metadata: {
  step_id: string;
  duration_ms?: number;
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('trail_step_completed', sanitize('trail_step_completed', metadata))
  );
}

// ─── Public: Phase 2 micro helpers — Sponsor (PRD §5.9) ──────────────────────

export async function logSponsorListViewed(): Promise<void> {
  await withClarity((svc) => svc.logEvent('sponsor_list_viewed'));
}

export async function logSponsorDetailViewed(metadata: { sponsor_id: string }): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('sponsor_detail_viewed', sanitize('sponsor_detail_viewed', metadata))
  );
}

export async function logSponsorCallRequested(metadata: { sponsor_id: string }): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('sponsor_call_requested', sanitize('sponsor_call_requested', metadata))
  );
}

// ─── Public: Phase 2 micro helpers — Notifications (PRD §5.10) ───────────────

export async function logNotificationOpened(metadata: { category: string }): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('notification_opened', sanitize('notification_opened', metadata))
  );
}

export async function logNotificationSettingsChanged(metadata: {
  category: string;
  enabled: boolean;
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent(
      'notification_settings_changed',
      sanitize('notification_settings_changed', metadata)
    )
  );
}

// ─── Public: Phase 2 micro helpers — Settings / account (PRD §5.11) ──────────

export async function logSettingsOpened(): Promise<void> {
  await withClarity((svc) => svc.logEvent('settings_opened'));
}

export async function logSettingsChanged(metadata: {
  field: 'name' | 'email' | 'phone' | 'username' | 'goal' | 'language' | 'password';
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('settings_changed', sanitize('settings_changed', metadata))
  );
}

export async function logAccountDeleteRequested(metadata: {
  step: 'first_screen' | 'confirmation';
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('account_delete_requested', sanitize('account_delete_requested', metadata))
  );
}

export async function logAccountDeleted(): Promise<void> {
  await withClarity((svc) => svc.logEvent('account_deleted'));
}

// ─── Public: Phase 2 micro helpers — Subscription extras (PRD §5.12) ─────────

/**
 * User commits to a package inside the native RC paywall. Fired from the
 * Phase 1.5 `Purchases.addPurchasesStartedListener` callback in
 * `services/revenueCatService.ts` — fires alongside the macro
 * `subscription_purchase_started`.
 */
export async function logSubscriptionPackageSelected(metadata: {
  package_id: string;
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent(
      'subscription_package_selected',
      sanitize('subscription_package_selected', metadata)
    )
  );
}

/**
 * User taps "Restore Purchases" outside the native paywall (the explicit
 * button in `app/perfil.tsx`). Native paywall's own Restore button is
 * invisible to JS and therefore not captured here.
 */
export async function logSubscriptionRestoreTapped(metadata: {
  result: 'tapped' | 'success' | 'failed';
}): Promise<void> {
  await withClarity((svc) =>
    svc.logEvent('subscription_restore_tapped', sanitize('subscription_restore_tapped', metadata))
  );
}

// ─── Public: Phase 2 micro helpers — CSAT (PRD §5.13) ────────────────────────

export async function logCsatDisplayed(metadata: {
  meeting_type?: 'video' | 'external' | 'appointment';
}): Promise<void> {
  await withClarity((svc) => svc.logEvent('csat_displayed', sanitize('csat_displayed', metadata)));
}

export async function logCsatDismissed(): Promise<void> {
  await withClarity((svc) => svc.logEvent('csat_dismissed'));
}

// ─── Public: Phase 3 nano helper — single canonical event (PRD §6) ───────────

/**
 * Nano UI interaction event. Emits a single canonical `ui_action` Clarity
 * event with `surface` + `element` + `action` + optional `variant` as
 * filter-dimension tags. Used to capture taps without exploding event-name
 * cardinality — one event, many filter values.
 *
 * Conventions (enforced by code review, not by the type system):
 * - `surface` is the friendly screen name (matches `pageNameForRoute()`
 *   output OR a stable shorthand like `'home'`, `'meeting_detail'`,
 *   `'profile'`).
 * - `element` is a stable, snake_case identifier for the UI element. NEVER
 *   include translated labels, user-typed text, names, emails, phone
 *   numbers, or any value derived from user content.
 * - `action` defaults to `'tapped'` — only set explicitly for non-tap
 *   gestures.
 * - `variant` is for A/B keys or visual-treatment tags (`'gem_icon'`,
 *   `'banner'`). Optional.
 *
 * Per-surface element-ID lists live in each surface's feature doc; the full
 * convention is documented in PRD_CLARITY_TAGGING.md §6.
 */
export async function logUiAction(metadata: {
  surface: string;
  element: string;
  action?: 'tapped' | 'long_pressed' | 'swiped' | 'expanded' | 'dismissed' | 'viewed';
  variant?: string;
}): Promise<void> {
  const payload = {
    surface: metadata.surface,
    element: metadata.element,
    action: metadata.action ?? 'tapped',
    ...(metadata.variant ? { variant: metadata.variant } : {}),
  };
  await withClarity((svc) => svc.logEvent('ui_action', sanitize('ui_action', payload)));
}
